/* Mosaic Art Studio — Wholesale order form.
   One page, many rows, one cart POST.

   Every row carries its B2B quantity rule (min / increment / max) and its
   volume price breaks as data attributes rendered by Liquid, so the stepper
   enforces the same rules Shopify enforces at checkout instead of letting a
   buyer type a quantity the cart will later reject. Quantities snap UP to the
   next valid step, never silently down, and 0 always means "not ordering".
   No dependencies, no storage APIs. */
(function () {
  'use strict';

  /* ---------- money ---------- */
  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return (format || '${{amount}}').replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      function (_, name) {
        switch (name) {
          case 'amount_no_decimals':
          case 'amount_no_decimals_with_comma_separator':
            return whole;
          case 'amount_with_comma_separator':
            return whole.replace(/,/g, '.') + ',' + parts[1];
          default:
            return whole + '.' + parts[1];
        }
      }
    );
  }

  /* ---------- quantity rules ---------- */

  // Valid quantities are 0, or min + (n * increment) capped at max.
  // Anything else snaps upward to the next valid step so the buyer never
  // ends up below a minimum they didn't know about.
  function snap(qty, rule) {
    if (!qty || qty <= 0) return 0;
    if (qty <= rule.min) return rule.min;

    var steps = Math.ceil((qty - rule.min) / rule.increment);
    var snapped = rule.min + steps * rule.increment;

    if (rule.max && snapped > rule.max) {
      var maxSteps = Math.floor((rule.max - rule.min) / rule.increment);
      snapped = rule.min + Math.max(0, maxSteps) * rule.increment;
    }
    return snapped;
  }

  function stepUp(qty, rule) {
    return snap(qty <= 0 ? rule.min : qty + rule.increment, rule);
  }

  function stepDown(qty, rule) {
    if (qty <= rule.min) return 0;
    return snap(qty - rule.increment, rule);
  }

  // Volume breaks are [minimumQuantity, unitPriceInCents], ascending.
  function unitPrice(qty, row) {
    var price = row.price;
    for (var i = 0; i < row.breaks.length; i++) {
      if (qty >= row.breaks[i][0]) price = row.breaks[i][1];
    }
    return price;
  }

  /* ---------- row ---------- */
  function buildRow(el) {
    var input = el.querySelector('[data-wo-input]');
    if (!input) return null;

    var maxAttr = parseInt(el.getAttribute('data-max'), 10);
    var breaks = [];
    try {
      breaks = JSON.parse(el.getAttribute('data-breaks') || '[]');
    } catch (e) {
      breaks = [];
    }

    return {
      el: el,
      input: input,
      id: el.getAttribute('data-variant-id'),
      price: parseInt(el.getAttribute('data-price'), 10) || 0,
      breaks: breaks,
      min: parseInt(el.getAttribute('data-min'), 10) || 1,
      increment: parseInt(el.getAttribute('data-increment'), 10) || 1,
      max: isNaN(maxAttr) ? 0 : maxAttr,
      lineEl: el.querySelector('[data-wo-line]'),
      noteEl: el.querySelector('[data-wo-note]'),
      breaksEl: el.querySelector('[data-wo-breaks]'),
      search: (el.getAttribute('data-search') || '').toLowerCase(),
      qty: 0
    };
  }

  function renderRow(row, format, snapped) {
    var qty = row.qty;
    var unit = unitPrice(qty, row);
    var total = unit * qty;

    row.el.setAttribute('data-active', qty > 0 ? 'true' : 'false');
    row.input.value = qty > 0 ? qty : '';

    if (row.lineEl) {
      row.lineEl.textContent = qty > 0 ? formatMoney(total, format) : '—';
      row.lineEl.setAttribute('data-empty', qty > 0 ? 'false' : 'true');
    }

    if (row.noteEl) {
      var note = row.noteEl.getAttribute('data-default') || '';
      if (snapped) note = row.noteEl.getAttribute('data-snapped-text') || note;
      row.noteEl.textContent = note;
      row.noteEl.setAttribute('data-snapped', snapped ? 'true' : 'false');
    }

    // Highlight whichever volume tier the current quantity actually earns.
    if (row.breaksEl) {
      var tiers = row.breaksEl.querySelectorAll('[data-break-min]');
      var earned = -1;
      for (var i = 0; i < tiers.length; i++) {
        if (qty >= parseInt(tiers[i].getAttribute('data-break-min'), 10)) earned = i;
      }
      for (var j = 0; j < tiers.length; j++) {
        tiers[j].classList.toggle('is-current', j === earned);
      }
    }
  }

  /* ---------- form ---------- */
  function initForm(root) {
    if (root.hasAttribute('data-wo-init')) return;
    root.setAttribute('data-wo-init', '');

    var format = root.getAttribute('data-money-format') || '${{amount}}';
    var addUrl = root.getAttribute('data-cart-add-url') || '/cart/add.js';
    var cartUrl = root.getAttribute('data-cart-url') || '/cart';
    var minOrder = parseInt(root.getAttribute('data-min-order'), 10) || 0;

    // Every row is searchable, but only rows with a quantity input are
    // orderable - an out-of-stock line still has to hide when filtered.
    var rowEls = Array.prototype.slice.call(root.querySelectorAll('[data-wo-row]'));
    if (!rowEls.length) return;

    var rows = [];
    rowEls.forEach(function (el) {
      var row = buildRow(el);
      if (row) rows.push(row);
    });

    var bar = root.querySelector('[data-wo-bar]');
    var statLines = root.querySelector('[data-wo-stat-lines]');
    var statUnits = root.querySelector('[data-wo-stat-units]');
    var statTotal = root.querySelector('[data-wo-stat-total]');
    var minNote = root.querySelector('[data-wo-min-note]');
    var submit = root.querySelector('[data-wo-submit]');
    var errorEl = root.querySelector('[data-wo-error]');
    var clearBtn = root.querySelector('[data-wo-clear]');
    var search = root.querySelector('[data-wo-search]');
    var empty = root.querySelector('[data-wo-empty]');

    function summarise() {
      var lines = 0, units = 0, total = 0;

      rows.forEach(function (row) {
        if (row.qty <= 0) return;
        lines += 1;
        units += row.qty;
        total += unitPrice(row.qty, row) * row.qty;
      });

      if (statLines) statLines.textContent = lines;
      if (statUnits) statUnits.textContent = units;
      if (statTotal) statTotal.textContent = formatMoney(total, format);

      var met = total >= minOrder;
      if (minNote) {
        minNote.setAttribute('data-met', met ? 'true' : 'false');
        minNote.textContent = met
          ? (minNote.getAttribute('data-met-text') || '')
          : (minNote.getAttribute('data-short-text') || '').replace(
              '[amount]',
              formatMoney(Math.max(0, minOrder - total), format)
            );
      }

      if (submit) submit.disabled = lines === 0 || !met || !catalogOk;
      if (bar) bar.setAttribute('data-visible', lines > 0 ? 'true' : 'false');
      if (errorEl && lines === 0) errorEl.textContent = '';
    }

    function setQty(row, next, snapped) {
      row.qty = next;
      renderRow(row, format, snapped);
      summarise();
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wo-step]');
      if (!btn || !root.contains(btn)) return;

      var el = btn.closest('[data-wo-row]');
      var row = rows.filter(function (r) { return r.el === el; })[0];
      if (!row) return;

      var dir = btn.getAttribute('data-wo-step');
      setQty(row, dir === 'up' ? stepUp(row.qty, row) : stepDown(row.qty, row), false);
    });

    rows.forEach(function (row) {
      // Snap on blur/change rather than on every keystroke, so typing "12"
      // isn't rewritten to the minimum the moment "1" is entered.
      function commit() {
        var raw = parseInt(row.input.value, 10);
        if (isNaN(raw) || raw <= 0) {
          setQty(row, 0, false);
          return;
        }
        var next = snap(raw, row);
        setQty(row, next, next !== raw);
      }
      row.input.addEventListener('change', commit);
      row.input.addEventListener('blur', commit);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        rows.forEach(function (row) { row.qty = 0; renderRow(row, format, false); });
        summarise();
      });
    }

    /* ---------- catalog safety check ---------- */

    // The B2B price list defines order minimums and case sizes. If a render
    // comes back with NONE of them, the catalog context didn't load and the
    // prices on screen are retail - the exact state that produces a trade
    // buyer paying retail. Say so and refuse to build the order.
    var catalogOk = true;
    if (root.getAttribute('data-verify-catalog') === 'true' && rowEls.length) {
      // Every RENDERED row, not just the orderable ones: a kit whose stock
      // has fallen below its own minimum is unorderable and so absent from
      // `rows`, and if enough of them are, the check would see no rules and
      // cry wolf over perfectly good wholesale prices.
      var hasRule = rowEls.some(function (el) {
        return (parseInt(el.getAttribute('data-min'), 10) || 1) > 1 ||
               (parseInt(el.getAttribute('data-increment'), 10) || 1) > 1;
      });
      if (!hasRule) {
        catalogOk = false;
        var warnEl = root.querySelector('[data-wo-catalog-warning]');
        var badgeEl = root.querySelector('[data-wo-badge]');
        if (warnEl) warnEl.hidden = false;
        if (badgeEl) badgeEl.hidden = true;
      }
    }

    /* ---------- facets ---------- */

    // Built from the rendered rows rather than hardcoded, so the filter list
    // always describes what this buyer's own catalog actually contains.
    var filterEl = root.querySelector('[data-wo-filters]');
    var groupEls = Array.prototype.slice.call(root.querySelectorAll('[data-wo-group]'));
    var active = { kind: 'all', value: '' };

    function facetsFrom(attr) {
      var seen = {};
      var order = [];
      rowEls.forEach(function (el) {
        var v = (el.getAttribute(attr) || '').trim();
        if (!v) return;
        if (!seen[v]) { seen[v] = 0; order.push(v); }
        seen[v] += 1;
      });
      return order.map(function (v) { return { value: v, count: seen[v] }; });
    }

    function matchesFilter(el) {
      if (active.kind === 'all') return true;
      var attr = active.kind === 'group' ? 'data-group' : 'data-type';
      return (el.getAttribute(attr) || '') === active.value;
    }

    function apply() {
      var term = search ? search.value.trim().toLowerCase() : '';
      var shown = 0;

      rowEls.forEach(function (el) {
        var key = (el.getAttribute('data-search') || '').toLowerCase();
        var hit = (!term || key.indexOf(term) !== -1) && matchesFilter(el);
        el.hidden = !hit;
        if (hit) shown += 1;
      });

      // Hide a group heading whose rows are all filtered out.
      groupEls.forEach(function (group) {
        group.hidden = group.querySelectorAll('[data-wo-row]:not([hidden])').length === 0;
      });

      if (empty) empty.hidden = shown !== 0;

      if (filterEl) {
        filterEl.querySelectorAll('[data-filter-kind]').forEach(function (btn) {
          var on = btn.getAttribute('data-filter-kind') === active.kind &&
                   btn.getAttribute('data-filter-value') === active.value;
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
    }

    function buildFilters() {
      if (!filterEl) return;

      var groups = facetsFrom('data-group');
      var types = facetsFrom('data-type');
      var html = '';

      function button(kind, value, label, count) {
        return '<button type="button" class="mas-wo-filter" data-filter-kind="' + kind +
          '" data-filter-value="' + String(value).replace(/"/g, '&quot;') +
          '" aria-pressed="false"><span>' + label + '</span><em>' + count + '</em></button>';
      }

      function escapeHtml(v) {
        return String(v).replace(/[&<>"]/g, function (c) {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
        });
      }

      html += button('all', '', 'All products', rowEls.length);

      if (groups.length > 1) {
        html += '<span class="mas-wo-filter-label">Collections</span>';
        groups.forEach(function (g) {
          html += button('group', g.value, escapeHtml(g.value), g.count);
        });
      }

      // One product type is no choice at all - only offer the facet when it
      // actually splits the list.
      if (types.length > 1) {
        html += '<span class="mas-wo-filter-label">Product type</span>';
        types.forEach(function (t) {
          html += button('type', t.value, escapeHtml(t.value), t.count);
        });
      }

      filterEl.innerHTML = html;

      filterEl.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-filter-kind]');
        if (!btn) return;
        active = {
          kind: btn.getAttribute('data-filter-kind'),
          value: btn.getAttribute('data-filter-value') || ''
        };
        apply();
      });
    }

    if (search) search.addEventListener('input', apply);
    buildFilters();
    apply();

    if (submit) {
      submit.addEventListener('click', function () {
        var items = rows
          .filter(function (row) { return row.qty > 0; })
          .map(function (row) { return { id: row.id, quantity: row.qty }; });

        if (!items.length) return;

        submit.disabled = true;
        if (errorEl) errorEl.textContent = '';
        var label = submit.textContent;
        submit.textContent = submit.getAttribute('data-loading-text') || 'Adding…';

        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (res) {
            return res.json().then(function (data) {
              if (!res.ok) throw new Error(data.description || data.message || 'Could not add these items.');
              return data;
            });
          })
          .then(function () { window.location.href = cartUrl; })
          .catch(function (err) {
            submit.disabled = false;
            submit.textContent = label;
            if (errorEl) errorEl.textContent = err.message;
          });
      });
    }

    rows.forEach(function (row) { renderRow(row, format, false); });
    summarise();
  }

  function initAll() {
    document.querySelectorAll('[data-wo-form]').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', initAll);
})();
